import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { User, AuthError } from '@supabase/supabase-js';
import { supabase } from '../supabase';

export interface UserProfile {
  id: string;
  email: string;
  username: string;
  full_name?: string;
  avatar_url?: string;
  bio?: string;
  role?: string;
  has_dev_access?: boolean;
  created_at: string;
  updated_at: string;
}

interface AuthContextType {
  user: User | null;
  profile: UserProfile | null;
  loading: boolean;
  profileLoading: boolean;
  isAdmin: boolean;
  hasDevAccess: boolean;
  error: string | null;
  signUp: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signIn: (emailOrUsername: string, password: string) => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<{ error: AuthError | null }>;
  updateProfile: (updates: Partial<UserProfile>) => Promise<{ error: Error | null }>;
  refreshProfile: () => Promise<void>;
  grantDevAccess: (userId: string, reason?: string) => Promise<{ error: Error | null }>;
  revokeDevAccess: (userId: string, reason?: string) => Promise<{ error: Error | null }>;
  getAllUsers: () => Promise<{ data: UserProfile[] | null; error: Error | null }>;
  resetAuthState: () => void;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: React.ReactNode;
}

// Constants for timeouts and retry logic
const OPERATION_TIMEOUT = 30000; // 30 seconds
const PROFILE_FETCH_TIMEOUT = 25000; // 25 seconds
const MAX_RETRY_ATTEMPTS = 3;

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Refs to track component mount state and ongoing operations
  const mountedRef = useRef(true);
  const operationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const profileTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Computed properties
  const isAdmin = profile?.role === 'admin';
  const hasDevAccess = isAdmin || profile?.has_dev_access === true;

  // Clear any existing timeout
  const clearOperationTimeout = useCallback(() => {
    if (operationTimeoutRef.current) {
      clearTimeout(operationTimeoutRef.current);
      operationTimeoutRef.current = null;
    }
  }, []);

  const clearProfileTimeout = useCallback(() => {
    if (profileTimeoutRef.current) {
      clearTimeout(profileTimeoutRef.current);
      profileTimeoutRef.current = null;
    }
  }, []);

  // Reset auth state function with cache clearing
  const resetAuthState = useCallback(() => {
    try {
      setUser(null);
      setProfile(null);
      setLoading(false);
      setProfileLoading(false);
      setError(null);
      
      // Clear all potentially problematic cache data
      try {
        localStorage.removeItem('supabase.auth.token');
        localStorage.removeItem('sb-project-auth-token');
        localStorage.removeItem('plugin_links'); // Clear old localStorage links
        localStorage.removeItem('auth_cache');
        sessionStorage.clear();
      } catch (e) {
        console.warn('Error clearing storage:', e);
      }
      
      clearOperationTimeout();
      clearProfileTimeout();
    } catch (error) {
      console.error('Error in resetAuthState:', error);
    }
  }, [clearOperationTimeout, clearProfileTimeout]);

  // Clear error state
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Safe state setter that checks if component is mounted
  const safeSetState = useCallback((setter: () => void) => {
    if (mountedRef.current) {
      setter();
    }
  }, []);

  // Enhanced operation wrapper with timeout and error handling
  const withTimeout = useCallback(async <T,>(
    operation: () => Promise<T>,
    timeoutMs: number = OPERATION_TIMEOUT,
    operationName: string = 'operation'
  ): Promise<T | null> => {
    try {
      const timeoutPromise = new Promise<never>((_, reject) => {
        const timeoutId = setTimeout(() => {
          reject(new Error(`${operationName} timed out after ${timeoutMs}ms`));
        }, timeoutMs);
        
        // Store timeout ID for cleanup
        if (operationName.includes('profile')) {
          profileTimeoutRef.current = timeoutId;
        } else {
          operationTimeoutRef.current = timeoutId;
        }
      });

      const result = await Promise.race([operation(), timeoutPromise]);
      
      // Clear timeout on success
      if (operationName.includes('profile')) {
        clearProfileTimeout();
      } else {
        clearOperationTimeout();
      }
      
      return result;
    } catch (error) {
      console.error(`${operationName} failed:`, error);
      
      // Clear timeouts
      clearOperationTimeout();
      clearProfileTimeout();
      
      // Set error state
      safeSetState(() => {
        setError(`${operationName} failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
      });
      
      return null;
    }
  }, [clearOperationTimeout, clearProfileTimeout, safeSetState]);

  // Enhanced profile fetching with timeout and retry logic
  const fetchProfile = useCallback(async (userId: string, userEmail?: string, retryCount = 0): Promise<UserProfile | null> => {
    if (!mountedRef.current) return null;

    try {
      console.log(`fetchProfile attempt ${retryCount + 1} for userId:`, userId);
      
      safeSetState(() => setProfileLoading(true));

      const profileOperation = async (): Promise<UserProfile | null> => {
        // Use the new get_or_create_profile function
        const { data, error } = await supabase.rpc('get_or_create_profile', {
          user_id: userId,
          user_email: userEmail || user?.email || ''
        });

        if (error) {
          console.error('Error fetching/creating profile:', error);
          
          // Fallback: try direct query
          const { data: directData, error: directError } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', userId)
            .single();

          if (directError && directError.code !== 'PGRST116') {
            console.error('Direct profile query failed:', directError);
            throw directError;
          }

          if (directData) {
            return directData;
          }

          // If still no profile, create one manually
          return await createProfileFallback(userId, userEmail || user?.email || '');
        }

        return data;
      };

      const result = await withTimeout(
        profileOperation,
        PROFILE_FETCH_TIMEOUT,
        'profile fetch'
      );

      if (result && mountedRef.current) {
        console.log('Profile fetched/created successfully:', result);
        safeSetState(() => {
          setProfile(result);
          setError(null); // Clear any previous errors
        });
        return result;
      } else if (retryCount < MAX_RETRY_ATTEMPTS - 1) {
        // Retry with exponential backoff
        const delay = Math.pow(2, retryCount) * 1000;
        console.log(`Retrying profile fetch in ${delay}ms...`);
        
        await new Promise(resolve => setTimeout(resolve, delay));
        return await fetchProfile(userId, userEmail, retryCount + 1);
      }

      return null;
    } catch (err) {
      console.error('Unexpected error in fetchProfile:', err);
      
      if (retryCount < MAX_RETRY_ATTEMPTS - 1) {
        const delay = Math.pow(2, retryCount) * 1000;
        console.log(`Retrying profile fetch after error in ${delay}ms...`);
        
        await new Promise(resolve => setTimeout(resolve, delay));
        return await fetchProfile(userId, userEmail, retryCount + 1);
      }
      
      safeSetState(() => {
        setError(`Failed to load profile after ${MAX_RETRY_ATTEMPTS} attempts`);
      });
      
      return null;
    } finally {
      if (mountedRef.current) {
        safeSetState(() => setProfileLoading(false));
        clearProfileTimeout();
      }
    }
  }, [user?.email, withTimeout, safeSetState, clearProfileTimeout]);

  // Fallback profile creation with error handling
  const createProfileFallback = async (userId: string, email: string): Promise<UserProfile | null> => {
    try {
      const defaultUsername = email.split('@')[0] || 'user';
      
      const newProfile = {
        id: userId,
        email: email,
        username: defaultUsername,
        full_name: '',
        avatar_url: null,
        bio: '',
        role: 'user',
        has_dev_access: false,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      const { data, error } = await supabase
        .from('profiles')
        .insert([newProfile])
        .select()
        .single();

      if (error) {
        console.error('Error creating fallback profile:', error);
        throw error;
      }

      return data;
    } catch (err) {
      console.error('Unexpected error in createProfileFallback:', err);
      throw err;
    }
  };

  // Refresh profile with error handling
  const refreshProfile = useCallback(async () => {
    if (user && mountedRef.current) {
      clearError();
      await fetchProfile(user.id, user.email);
    }
  }, [user, fetchProfile, clearError]);

  // Admin function to get all users with timeout
  const getAllUsers = useCallback(async (): Promise<{ data: UserProfile[] | null; error: Error | null }> => {
    try {
      if (!isAdmin) {
        return { data: null, error: new Error('Access denied: Admin privileges required') };
      }

      const operation = async () => {
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .order('created_at', { ascending: false });

        if (error) throw error;
        return data;
      };

      const result = await withTimeout(operation, OPERATION_TIMEOUT, 'get all users');
      
      if (result) {
        return { data: result, error: null };
      } else {
        return { data: null, error: new Error('Operation timed out or failed') };
      }
    } catch (error) {
      return { data: null, error: error as Error };
    }
  }, [isAdmin, withTimeout]);

  // Enhanced auth functions with timeout and error handling
  const grantDevAccess = useCallback(async (userId: string, reason = 'Granted by admin'): Promise<{ error: Error | null }> => {
    try {
      if (!isAdmin) {
        return { error: new Error('Access denied: Admin privileges required') };
      }

      const operation = async () => {
        const { error } = await supabase.rpc('grant_dev_access', {
          target_user_id: userId,
          reason: reason
        });
        if (error) throw error;
      };

      const result = await withTimeout(operation, OPERATION_TIMEOUT, 'grant dev access');
      
      if (result !== null) {
        // If we're updating our own profile, refresh it
        if (userId === user?.id) {
          await refreshProfile();
        }
        return { error: null };
      } else {
        return { error: new Error('Operation timed out or failed') };
      }
    } catch (error) {
      return { error: error as Error };
    }
  }, [isAdmin, user?.id, refreshProfile, withTimeout]);

  const revokeDevAccess = useCallback(async (userId: string, reason = 'Revoked by admin'): Promise<{ error: Error | null }> => {
    try {
      if (!isAdmin) {
        return { error: new Error('Access denied: Admin privileges required') };
      }

      const operation = async () => {
        const { error } = await supabase.rpc('revoke_dev_access', {
          target_user_id: userId,
          reason: reason
        });
        if (error) throw error;
      };

      const result = await withTimeout(operation, OPERATION_TIMEOUT, 'revoke dev access');
      
      if (result !== null) {
        // If we're updating our own profile, refresh it
        if (userId === user?.id) {
          await refreshProfile();
        }
        return { error: null };
      } else {
        return { error: new Error('Operation timed out or failed') };
      }
    } catch (error) {
      return { error: error as Error };
    }
  }, [isAdmin, user?.id, refreshProfile, withTimeout]);

  // Enhanced session recovery with better error handling
  useEffect(() => {
    let mounted = true;
    
    const initializeAuth = async () => {
      try {
        setLoading(true);
        
        // Clear any stale data first
        if (performance.navigation?.type === 1) { // Page refresh
          console.log('Page refresh detected, clearing stale data');
          try {
            localStorage.removeItem('plugin_links'); // Remove old localStorage data
            sessionStorage.removeItem('temp_auth_data');
          } catch (e) {
            console.warn('Error clearing stale data:', e);
          }
        }
        
        // Get initial session
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.error('Session error:', sessionError);
          if (mounted) {
            setError('Session error: ' + sessionError.message);
            setUser(null);
            setProfile(null);
          }
          return;
        }

        if (session?.user && mounted) {
          setUser(session.user);
          await fetchProfile(session.user.id, session.user.email);
        } else if (mounted) {
          setUser(null);
          setProfile(null);
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        if (mounted) {
          setError('Failed to initialize authentication');
          resetAuthState();
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    initializeAuth();
    
    return () => {
      mounted = false;
    };
  }, [fetchProfile, resetAuthState]);

  // Set up real-time subscription for profile changes with error handling
  useEffect(() => {
    if (!user?.id || !mountedRef.current) return;

    try {
      const subscription = supabase
        .channel('profile-changes')
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'profiles',
            filter: `id=eq.${user.id}`
          },
          (payload) => {
            try {
              console.log('Profile updated via real-time:', payload);
              if (payload.new && mountedRef.current) {
                safeSetState(() => {
                  setProfile(payload.new as UserProfile);
                  setError(null); // Clear any errors when profile updates successfully
                });
              }
            } catch (error) {
              console.error('Real-time profile update error:', error);
            }
          }
        )
        .subscribe();

      return () => {
        subscription.unsubscribe();
      };
    } catch (error) {
      console.error('Failed to set up real-time subscription:', error);
      // Don't set error state for real-time subscription failures
    }
  }, [user?.id, safeSetState]);

  // Enhanced auth functions with timeout
  const signUp = useCallback(async (email: string, password: string) => {
    try {
      clearError();
      
      const operation = async () => {
        // Determine the correct redirect URL based on environment
        const getRedirectUrl = () => {
          const currentUrl = window.location.origin;
          
          // If we're on Netlify (contains netlify.app) or production domain
          if (currentUrl.includes('netlify.app') || currentUrl.includes('sunny-cupcake-1b862a.netlify.app')) {
            return 'https://sunny-cupcake-1b862a.netlify.app/auth/callback';
          }
          
          // For local development
          return `${currentUrl}/auth/callback`;
        };

        const { error } = await supabase.auth.signUp({ 
          email, 
          password,
          options: {
            emailRedirectTo: getRedirectUrl()
          }
        });
        
        if (error) throw error;
        return { error: null };
      };

      const result = await withTimeout(operation, OPERATION_TIMEOUT, 'sign up');
      return result || { error: { message: 'Sign up timed out' } as AuthError };
    } catch (error) {
      return { error: error as AuthError };
    }
  }, [clearError, withTimeout]);

  const signIn = useCallback(async (emailOrUsername: string, password: string) => {
    try {
      clearError();
      
      const operation = async () => {
        let email = emailOrUsername;
        
        // If it doesn't contain @, it's a username - look up the email
        if (!emailOrUsername.includes('@')) {
          const { data: profileData, error: profileError } = await supabase
            .from('profiles')
            .select('email')
            .eq('username', emailOrUsername)
            .single();

          if (profileError || !profileData) {
            throw new Error('Username not found');
          }
          
          email = profileData.email;
        }

        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        
        if (error) throw error;
        
        // Immediately set the user and fetch profile on successful login
        if (data.user && mountedRef.current) {
          setUser(data.user);
          await fetchProfile(data.user.id, data.user.email);
        }
        
        return { error: null };
      };

      const result = await withTimeout(operation, OPERATION_TIMEOUT, 'sign in');
      return result || { error: { message: 'Sign in timed out' } as AuthError };
    } catch (error) {
      return { error: error as AuthError };
    }
  }, [clearError, withTimeout, fetchProfile]);

  const signOut = useCallback(async () => {
    try {
      clearError();
      
      const operation = async () => {
        const { error } = await supabase.auth.signOut();
        if (error) throw error;
        return { error: null };
      };

      const result = await withTimeout(operation, OPERATION_TIMEOUT, 'sign out');
      
      // Always reset state on sign out, even if the operation failed
      await resetAuthState();
      
      return result || { error: { message: 'Sign out timed out' } as AuthError };
    } catch (error) {
      // Always reset state on sign out, even if there was an error
      await resetAuthState();
      return { error: error as AuthError };
    }
  }, [clearError, withTimeout, resetAuthState]);

  // Update profile with timeout
  const updateProfile = useCallback(async (updates: Partial<UserProfile>) => {
    try {
      if (!user) {
        return { error: new Error('Must be logged in to update profile') };
      }

      if (!profile) {
        return { error: new Error('Profile not loaded') };
      }

      clearError();

      const operation = async () => {
        // Check for username conflicts
        if (updates.username && updates.username !== profile.username) {
          const { data: existingUser, error: checkError } = await supabase
            .from('profiles')
            .select('id')
            .eq('username', updates.username)
            .neq('id', user.id)
            .single();

          if (checkError && checkError.code !== 'PGRST116') {
            throw checkError;
          }

          if (existingUser) {
            throw new Error('Username is already taken');
          }
        }

        const { error } = await supabase
          .from('profiles')
          .update({ ...updates, updated_at: new Date().toISOString() })
          .eq('id', user.id);

        if (error) throw error;

        // Update local state
        if (mountedRef.current) {
          safeSetState(() => {
            setProfile(prevProfile => {
              if (!prevProfile) return prevProfile;
              return { ...prevProfile, ...updates };
            });
          });
        }

        return { error: null };
      };

      const result = await withTimeout(operation, OPERATION_TIMEOUT, 'update profile');
      return result || { error: new Error('Update profile timed out') };
    } catch (error) {
      return { error: error as Error };
    }
  }, [user, profile, clearError, withTimeout, safeSetState]);

  const contextValue: AuthContextType = {
    user,
    profile,
    loading,
    profileLoading,
    isAdmin,
    hasDevAccess,
    error,
    signUp,
    signIn,
    signOut,
    updateProfile,
    refreshProfile,
    grantDevAccess,
    revokeDevAccess,
    getAllUsers,
    resetAuthState,
    clearError,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Export the useAuth hook separately to fix Fast Refresh issues
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 